import api from 'services/api';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {
  CONTEXT_MENU_FILTER_KEYS, eq
} from 'store/Filters/constants';
import {
  selectQueryParam, selectIsGrouping, selectExpandedQueryParam, selectAdvancedFilterQueryParam, selectAdvancedExpandedQueryParam
} from 'store/Filters/selectors';
import { selectManageFields } from 'store/Preferences/manage/contextSelectors';
import {
  setPageNumber,
  setFilter,
  setOrderBy,
  setGroupBy,
  clearGroupBy,
  expandGroupBy,
  setFilterError,
  clearFilters,
  addFilter
} from 'store/Filters/actions';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import { selectExploreGroupPayload } from 'store/GroupVulns/selectors';
import { selectPathname } from 'store/Router/selectors';
import {
  SEARCH_VULNS_START,
  GET_VULNS_SUCCESS_MANAGE,
  GET_GROUPED_DATA_SUCCESS,
  GET_GROUPED_DATA_START,
  SET_VULNS_FILTER_ERROR
  // ADD_MOCK_VULN
} from 'store/Manage/types';
import { withVulnColumns } from './vulnColumns';
import { normalizeVulnIds } from 'Common/Functions/vulnerabilityId';

import { onGetVulnsFail } from './actions';

export const setVulnsFilterError = () => async (dispatch) => dispatch({ type: SET_VULNS_FILTER_ERROR });

const exploreRoutePattern = /^\/vulns\/([^/]+)\/groups\/([^/]+)\/?$/;
const manageRoutePattern = /^\/manage\/([^/]+)(?:\/[^/]+)?\/?$/;

const opsAllowingEmptyValue = new Set(['is_null', 'is_not_null']);

const hasEmptyStringValue = (value) => typeof value === 'string' && value.trim() === '';

const sanitizeFilterNode = (node) => {
  if (!node || typeof node !== 'object') return node;

  if (node.and || node.or) {
    const key = node.and ? 'and' : 'or';
    const items = (Array.isArray(node[key]) ? node[key] : [])
      .map(sanitizeFilterNode)
      .filter(Boolean);

    if (items.length === 0) return null;
    if (items.length === 1) return items[0];

    return { [key]: items };
  }

  if (!Object.prototype.hasOwnProperty.call(node, 'op')) return node;
  if (opsAllowingEmptyValue.has(node.op)) return node;

  if (hasEmptyStringValue(node.val)) return null;

  if (node.val && typeof node.val === 'object' && Object.prototype.hasOwnProperty.call(node.val, 'val')) {
    const nestedOp = get(node, 'val.op', '');
    const nestedVal = get(node, 'val.val', null);
    if (!opsAllowingEmptyValue.has(nestedOp) && hasEmptyStringValue(nestedVal)) return null;
  }

  return node;
};

const sanitizeQueryFilters = (queryParam) => {
  const baseQuery = queryParam && typeof queryParam === 'object' ? queryParam : {};
  const currentFilters = Array.isArray(baseQuery.filters) ? baseQuery.filters : [];

  if (isEmpty(currentFilters)) return baseQuery;

  const sanitizedFilters = currentFilters
    .map(sanitizeFilterNode)
    .filter(Boolean);

  if (sanitizedFilters.length === 0) {
    const { filters, ...restQuery } = baseQuery;
    return restQuery;
  }

  return {
    ...baseQuery,
    filters: sanitizedFilters
  };
};

const getExploreGroupIdFromPath = (pathname = '') => {
  const match = pathname.match(exploreRoutePattern);
  if (!match) return null;
  const routeGroupId = match[2];
  return routeGroupId && routeGroupId !== 'new' ? routeGroupId : null;
};

const getVulnsRequestContextKey = (pathname = '') => {
  const exploreMatch = pathname.match(exploreRoutePattern);
  if (exploreMatch) {
    const [, ws, groupId] = exploreMatch;
    return `explore:${ws}:${groupId}`;
  }

  const manageMatch = pathname.match(manageRoutePattern);
  if (manageMatch) {
    const [, ws] = manageMatch;
    return `manage:${ws}`;
  }

  return pathname;
};

const normalizeGroupIdentifier = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const buildExploreGroupFilter = ({ groupHeaderId, groupId }) => {
  const parsedGroupId = normalizeGroupIdentifier(groupId);
  if (parsedGroupId !== null) {
    return {
      name: 'group_id',
      op: eq.name.value,
      val: parsedGroupId
    };
  }

  const parsedGroupHeaderId = normalizeGroupIdentifier(groupHeaderId);
  if (parsedGroupHeaderId !== null) {
    return {
      name: 'id',
      op: eq.name.value,
      val: parsedGroupHeaderId
    };
  }

  return null;
};

export const withExploreGroupFilter = (queryParam, state) => {
  const pathname = selectPathname(state);
  const exploreGroupId = getExploreGroupIdFromPath(pathname);
  if (!exploreGroupId) return queryParam;
  const explorePayload = selectExploreGroupPayload(state);
  const payloadGroupId = get(explorePayload, 'groupId', null);
  const payloadGroupHeaderId = get(explorePayload, 'groupHeaderId', null);
  const groupId = payloadGroupId !== null && String(payloadGroupId) === String(exploreGroupId)
    ? payloadGroupId
    : exploreGroupId;

  const baseQuery = queryParam && typeof queryParam === 'object' ? queryParam : {};
  const exploreFilter = buildExploreGroupFilter({
    groupHeaderId: payloadGroupHeaderId,
    groupId
  });
  if (!exploreFilter) return baseQuery;

  const currentFilters = Array.isArray(baseQuery.filters) ? baseQuery.filters : [];

  if (isEmpty(currentFilters)) {
    return {
      ...baseQuery,
      filters: [exploreFilter]
    };
  }

  return {
    ...baseQuery,
    filters: [
      {
        and: [
          ...currentFilters,
          exploreFilter
        ]
      }
    ]
  };
};

const buildWorkspaceManageDefaultFilter = () => ({
  or: [
    {
      name: 'is_main',
      op: eq.name.value,
      val: true
    },
    {
      name: 'is_main',
      op: 'is_null',
      val: true
    }
  ]
});

const withWorkspaceManageDefaultFilter = (queryParam, state) => {
  const pathname = selectPathname(state);
  if (!manageRoutePattern.test(pathname)) return queryParam;

  const baseQuery = queryParam && typeof queryParam === 'object' ? queryParam : {};
  const currentFilters = Array.isArray(baseQuery.filters) ? baseQuery.filters : [];
  const mainOrUngroupedFilter = buildWorkspaceManageDefaultFilter();

  if (isEmpty(currentFilters)) {
    return {
      ...baseQuery,
      filters: [mainOrUngroupedFilter]
    };
  }

  return {
    ...baseQuery,
    filters: [
      {
        and: [
          ...currentFilters,
          mainOrUngroupedFilter
        ]
      }
    ]
  };
};

const normalizeVulnValue = (vuln) => {
  const value = get(vuln, 'value', {});
  return normalizeVulnIds(value);
};

export function newGetVulns (ws) {
  return async (dispatch, getState) => {
    dispatch({ type: SEARCH_VULNS_START });
    const state = getState();
    const requestContextKey = getVulnsRequestContextKey(selectPathname(state));
    const workspaceSelected = selectCurrentWorkspace(state);
    let advancedFilterQueryParam = [];

    try {
      advancedFilterQueryParam = selectAdvancedFilterQueryParam(state, 'vulns');
    } catch (e) {
      dispatch(setFilterError('vulns', 'Syntax error. Please try again. For further help check our documentation'));
      return dispatch(setVulnsFilterError());
    }

    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectQueryParam('vulns', state);
    const fields = selectManageFields(state);
    const queryParamBase = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;
    const groupBy = selectIsGrouping('vulns', state);
    let queryParam = sanitizeQueryFilters(queryParamBase);
    queryParam = withExploreGroupFilter(queryParam, state);

    if (!groupBy) {
      queryParam = withWorkspaceManageDefaultFilter(queryParam, state);
    }

    // Manage's Name cell renders grouping metadata (`is_main`, `group_*`) that
    // is part of the vulnerability row, not valid API `columns`.
    queryParam = withVulnColumns(queryParam, fields, { keepFullPayload: true });

    try {
      const response = await api.manage.newGetVulns(ws || workspaceSelected, queryParam);
      const responseContextKey = getVulnsRequestContextKey(selectPathname(getState()));
      if (responseContextKey !== requestContextKey) return dispatch({ type: SET_VULNS_FILTER_ERROR });

      if (!response.status || response.status !== 'canceled') {
        const vulns = get(response, 'vulnerabilities', []);
        const result = vulns.map(normalizeVulnValue);
        const vulnsCountArray = vulns.map((vuln) => vuln.value.count);
        const vulnsCountGroupBy = vulnsCountArray.reduce((prev, cur) => prev + cur, 0);
        return dispatch({
          type: GET_VULNS_SUCCESS_MANAGE,
          data: result,
          vulnsCount: groupBy ? vulnsCountGroupBy : response.count
        });
      }
    } catch (e) {
      const errorMessage = get(e, 'message', 'Failed to obtain vulnerabilities.');
      if (errorMessage.includes('filter')) {
        dispatch(setFilterError('vulns', 'Invalid filters. Please try again. For further help check our documentation'));
        return dispatch(setVulnsFilterError());
      }
      return dispatch(onGetVulnsFail(errorMessage));
    }
  };
}

function getExpandedGroupByData (index) {
  return async (dispatch, getState) => {
    dispatch({ type: GET_GROUPED_DATA_START });
    const state = getState();
    const requestContextKey = getVulnsRequestContextKey(selectPathname(state));

    let advancedFilterQueryParam = [];
    try {
      advancedFilterQueryParam = selectAdvancedExpandedQueryParam(state, 'vulns');
    } catch (e) {
      dispatch(setFilterError('vulns', 'Syntax error. Please try again. For further help check our documentation'));
      return dispatch(setVulnsFilterError());
    }
    const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
    const standardQueryParam = selectExpandedQueryParam('vulns', state);
    const fields = selectManageFields(state);
    const queryParamBase = hasAdvancedFilter ? advancedFilterQueryParam : standardQueryParam;
    // Expanded Manage rows need the same grouping metadata as the main table.
    const queryParam = withVulnColumns(withExploreGroupFilter(sanitizeQueryFilters(queryParamBase), state), fields, { keepFullPayload: true });

    const { faraday } = state;

    try {
      const response = await api.manage.newGetVulns(faraday.workspaceSelected, queryParam);
      const responseContextKey = getVulnsRequestContextKey(selectPathname(getState()));
      if (responseContextKey !== requestContextKey) return dispatch({ type: SET_VULNS_FILTER_ERROR });

      if (!response.status || response.status !== 'canceled') {
        const result = response.vulnerabilities.map(normalizeVulnValue);

        return dispatch({
          type: GET_GROUPED_DATA_SUCCESS,
          data: result,
          index
        });
      }
    } catch (e) {
      const errorMessage = get(e, 'message', 'Failed to obtain vulnerabilities.');
      if (errorMessage.includes('filter')) {
        dispatch(setFilterError('vulns', 'Invalid filters. Please try again. For further help check our documentation'));
        return dispatch(setVulnsFilterError());
      }
      return dispatch(onGetVulnsFail(errorMessage));
    }
  };
}

// CONTEXT MENU FILTERING FUNCTIONS -------------------------------------------------

export const contextMenuFilterByName = (selectedVuln) => {
  const filter = get(CONTEXT_MENU_FILTER_KEYS, 'name.filter', null);
  const nameValue = get(selectedVuln, 'name', '');
  filter.val = filter.op === 'ilike' ? `%${nameValue}%` : nameValue;
  return filter;
};
export const contextMenuFilterByAsset = (selectedVuln) => {
  const filter = get(CONTEXT_MENU_FILTER_KEYS, 'asset.filter', null);
  const targetValue = get(selectedVuln, 'target', '');
  filter.val = targetValue;
  return filter;
};
export const contextMenuFilterByDate = (selectedVuln) => {
  const filter = get(CONTEXT_MENU_FILTER_KEYS, 'date.filter', null);
  const date_parsed = new Date(get(selectedVuln, 'date', ''));
  const dateValue = `${date_parsed.getFullYear()}-${date_parsed.getMonth() + 1}-${date_parsed.getDate()}`;
  filter.val = dateValue;
  return filter;
};
export const contextMenuFilterByStatus = (selectedVuln) => {
  const filter = get(CONTEXT_MENU_FILTER_KEYS, 'status.filter', null);
  filter.val = selectedVuln.status;
  return filter;
};
export const contextMenuFilterByService = (selectedVuln) => {
  const filter = get(CONTEXT_MENU_FILTER_KEYS, 'service.filter', null);
  const serviceValue = get(selectedVuln, 'service.name', '');
  filter.val = {
    name: 'name',
    op: eq.name.value,
    val: serviceValue
  };
  return filter;
};
const contextMenuFilterByTags = (selectedVuln) => {
  const tagsArray = get(selectedVuln, 'tags', []);
  let tagFilters = [];
  tagsArray.forEach((tagFilter) => {
    const filter = {
      name: 'tags__name',
      op: eq.name.value,
      val: tagFilter
    };
    tagFilters = [...tagFilters, filter];
  });
  return tagFilters;
};

export const contextMenufilteringFunctions = {
  [CONTEXT_MENU_FILTER_KEYS.name.key.name]: contextMenuFilterByName,
  [CONTEXT_MENU_FILTER_KEYS.asset.key.name]: contextMenuFilterByAsset,
  [CONTEXT_MENU_FILTER_KEYS.date.key.name]: contextMenuFilterByDate,
  [CONTEXT_MENU_FILTER_KEYS.status.key.name]: contextMenuFilterByStatus,
  [CONTEXT_MENU_FILTER_KEYS.service.key.name]: contextMenuFilterByService,
  [CONTEXT_MENU_FILTER_KEYS.tags.key.name]: contextMenuFilterByTags
};

export function setContextMenuFilter (filterKey, selectedVuln) {
  return (dispatch) => {
    const newFilter = contextMenufilteringFunctions[filterKey](selectedVuln);
    if (filterKey === 'tags') {
      dispatch(clearFilters('vulns'));
      newFilter.forEach((tagFilter) => {
        dispatch(addFilter('vulns', tagFilter));
      });
    } else if (newFilter) dispatch(setFilter('vulns', newFilter));
    dispatch(newGetVulns());
  };
}
// ------------------------------------------------------------------------------

export function setPage (pageNumber) {
  return (dispatch) => {
    dispatch(setPageNumber('vulns', pageNumber));
    dispatch(newGetVulns());
  };
}

export function setOrderByVulns (sorting) {
  return (dispatch, getState) => {
    const state = getState();
    const isGrouping = selectIsGrouping('vulns', state);

    dispatch(setOrderBy('vulns', sorting));
    if (!isGrouping) dispatch(newGetVulns());
  };
}

export function setGroupByVulns (group_by) {
  return async (dispatch) => {
    const field = get(group_by, '[0].field', '');
    await dispatch(setGroupBy('vulns', group_by));
    if (field) await dispatch(setOrderBy('vulns', [{ id: field, desc: field === 'severity' }]));
    dispatch(newGetVulns());
  };
}

export function clearGroupByVulns () {
  return async (dispatch) => {
    await dispatch(clearGroupBy('vulns'));
  };
}

export function expandGroupByVulns (index, data) {
  return async (dispatch) => {
    await dispatch(expandGroupBy('vulns', data));
    dispatch(getExpandedGroupByData(index));
  };
}
